import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Diamond as DiamondIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const ServicePackages = ({ onPackageSelect, selectedPackage, isAuthenticated = true, onRequireLogin, isPaid }) => {
  const theme = useTheme();

  // 解析 selectedPackage，兼容字符串和对象
  let selectedPkgObj = selectedPackage;
  if (typeof selectedPackage === 'string') {
    try {
      selectedPkgObj = JSON.parse(selectedPackage);
    } catch (e) {
      selectedPkgObj = null;
    }
  }

  // 移除 popular 字段
  const packages = [
    {
      id: 'basic',
      name: '基础申请套餐',
      price: '9800',
      originalPrice: '9800',
      icon: <CheckIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.primary.main,
      description: '适合有一定准备基础的学生',
      features: [
        '专业选择咨询',
        '申请材料指导',
        '1所大学申请',
        '签证申请指导',
        '入学手续协助',
        '3个月服务期',
        '邮件支持',
      ],
      excluded: [
        '语言培训',
        '面试辅导',
        '住宿安排',
      ]
    },
    {
      id: 'premium',
      name: '全程无忧套餐',
      price: '10800',
      originalPrice: '10800',
      icon: <StarIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.secondary.main,
      description: '最受欢迎，性价比最高的选择',
      features: [
        '专业选择咨询',
        '申请材料指导',
        '3所大学申请',
        '签证申请指导',
        '语言培训支持',
        '面试辅导',
        '入学手续协助',
        '6个月服务期',
        '24/7在线支持',
        '住宿推荐',
      ],
      excluded: []
    },
    {
      id: 'vip',
      name: 'VIP定制套餐',
      price: '13800',
      originalPrice: '13800',
      icon: <DiamondIcon sx={{ fontSize: 40 }} />,
      color: theme.palette.warning.main,
      description: '一对一专属服务，成功率最高',
      features: [
        '专业选择咨询',
        '申请材料指导',
        '5所大学申请',
        '签证申请指导',
        '语言培训支持',
        '面试辅导',
        '入学手续协助',
        '12个月服务期',
        '24/7专属顾问',
        '住宿安排',
        '接机服务',
        '生活指导',
        '学业跟踪',
      ],
      excluded: []
    },
  ];

  // 动态设置 popular 字段
  const packagesWithPopular = packages.map((pkg, idx) => {
    if (selectedPkgObj) {
      return {
        ...pkg,
        popular: pkg.id === selectedPkgObj.id
      };
    } else {
      // 没有 selectedPackage，默认第二个套餐 popular:true
      return {
        ...pkg,
        popular: idx === 1
      };
    }
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <Box sx={{ py: 8, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
              }}
            >
              服务套餐
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              选择最适合您的申请服务套餐，我们为不同需求的学生提供专业解决方案
            </Typography>
          </Box>

          {/* Packages Grid */}
          <Grid container spacing={4}>
            {packagesWithPopular.map((pkg, index) => (
              <Grid item xs={12} md={4} key={pkg.id}>
                <motion.div variants={cardVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      border: pkg.popular ? `3px solid ${pkg.color}` : `1px solid ${alpha(pkg.color, 0.2)}`,
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 16px 40px ${alpha(pkg.color, 0.2)}`,
                      },
                    }}
                  >
                    {/* Popular Badge */}
                    {pkg.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: pkg.color,
                          color: 'white',
                          px: 3,
                          py: 1,
                          borderRadius: 20,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <TrophyIcon sx={{ fontSize: 16 }} />
                        最受欢迎
                      </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                      {/* Package Icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          backgroundColor: alpha(pkg.color, 0.1),
                          color: pkg.color,
                          mx: 'auto',
                          mb: 3,
                        }}
                      >
                        {pkg.icon}
                      </Box>

                      {/* Package Name */}
                      <Typography
                        variant="h4"
                        sx={{
                          mb: 1,
                          fontWeight: 700,
                          textAlign: 'center',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {pkg.name}
                      </Typography>

                      {/* Package Description */}
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3,
                          textAlign: 'center',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {pkg.description}
                      </Typography>

                      {/* Price */}
                      <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              color: pkg.color,
                            }}
                          >
                            ￥{pkg.price}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              textDecoration: 'line-through',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            ￥{pkg.originalPrice}
                          </Typography>
                        </Box>
                        <Chip
                          label="限时优惠"
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      {/* Features List */}
                      <List sx={{ mb: 3 }}>
                        {pkg.features.map((feature, featureIndex) => (
                          <ListItem key={featureIndex} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckIcon sx={{ fontSize: 20, color: theme.palette.success.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              sx={{
                                '& .MuiTypography-root': {
                                  fontSize: '0.9rem',
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      {/* CTA Button */}
                      {isPaid ? (
                        selectedPkgObj?.id === pkg.id && (
                          <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled
                            sx={{
                              py: 1.5,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: '1rem',
                              backgroundColor: theme.palette.success.main,
                              '&:hover': {
                                backgroundColor: theme.palette.success.main,
                              },
                            }}
                          >
                            已购买此套餐
                          </Button>
                        )
                      ) : (
                        <Button
                          variant={selectedPkgObj?.id === pkg.id ? 'contained' : (pkg.popular ? 'contained' : 'outlined')}
                          fullWidth
                          size="large"
                          onClick={() => {
                            if (!isAuthenticated) {
                              onRequireLogin && onRequireLogin();
                              return;
                            }
                            onPackageSelect && onPackageSelect(pkg);
                          }}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: '1rem',
                            ...(selectedPackage?.id === pkg.id
                              ? {
                                  backgroundColor: theme.palette.success.main,
                                  '&:hover': {
                                    backgroundColor: theme.palette.success.dark,
                                  },
                                }
                              : pkg.popular
                              ? {
                                  backgroundColor: pkg.color,
                                  '&:hover': {
                                    backgroundColor: pkg.color,
                                    opacity: 0.9,
                                  },
                                }
                              : {
                                  borderColor: pkg.color,
                                  color: pkg.color,
                                  '&:hover': {
                                    backgroundColor: alpha(pkg.color, 0.1),
                                    borderColor: pkg.color,
                                  },
                                }),
                          }}
                        >
                          {selectedPackage?.id === pkg.id ? '已选择此套餐' : '选择此套餐'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Additional Info */}
          <Box
            sx={{
              textAlign: 'center',
              mt: 6,
              p: 4,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              🎁 限时优惠活动
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: theme.palette.text.secondary,
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              现在预订任意套餐，即可享受以下额外福利：
              免费语言水平测试 | 免费专业匹配评估 | 优先处理申请 | 专属微信群服务
            </Typography>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 25,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              联系顾问定制方案
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ServicePackages;
